import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUser, FaSearch, FaMapMarkerAlt, FaEnvelope, FaPhone, FaCalendarAlt,
  FaMicrophone, FaRedo, FaChevronLeft, FaChevronRight
} from "react-icons/fa";

const TotalRaiseVoiceUser = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const fetchRaiseVoiceUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/super/admin/get/users/raise/your/voice`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const usersData = response.data.users || response.data.data || response.data || [];
      if (Array.isArray(usersData)) {
        setUsers(usersData);
      } else {
        setUsers([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to fetch Raise Voice users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRaiseVoiceUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(term)) ||
      (user.email && user.email.toLowerCase().includes(term)) ||
      (user.phoneNo && user.phoneNo.toLowerCase().includes(term)) ||
      (user.city && user.city.toLowerCase().includes(term)) ||
      (user.state && user.state.toLowerCase().includes(term)) ||
      (user.residenceAddress && user.residenceAddress.toLowerCase().includes(term))
    );
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans">
      <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-600 z-50"></div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-indigo-500/20 ring-4 ring-indigo-50">
            <FaMicrophone />
          </div>
          <div>
            <h1 className="text-3xl font-black text-[#1e293b] tracking-tight">
              Community Voices
            </h1>
            <p className="text-[#94a3b8] text-xs font-bold tracking-tight mt-1 uppercase tracking-widest flex items-center gap-2">
              {users.length} active voice profiles registered
            </p>
          </div>
        </div>
        <button
          onClick={fetchRaiseVoiceUsers}
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all font-semibold active:scale-95"
        >
          <FaRedo className={`${loading ? 'animate-spin' : ''}`} />
          Refresh List
        </button>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-500/5 border border-[#f1f5f9] overflow-hidden">
        <div className="p-6 md:p-8 border-b border-[#f1f5f9] flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-lg shadow-sm">
              <FaSearch />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1e293b]">Voice Directory</h3>
              <p className="text-xs text-[#94a3b8] font-bold">{filteredUsers.length} active citizens</p>
            </div>
          </div>
          <div className="relative w-full md:w-96 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-indigo-500 transition-colors" />
            <input
              type="text"
              placeholder="Search by name, contact or location..."
              className="w-full pl-12 pr-4 py-3.5 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 focus:bg-white focus:border-indigo-200 transition-all text-sm font-medium text-[#334155]"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Citizen Profile</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest text-center">Status</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Contact Index</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Residency</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Onboarding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="5" className="px-8 py-8"><div className="h-10 bg-gray-100 rounded-2xl w-full"></div></td>
                  </tr>
                ))
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-8 py-20 text-center">
                    <p className="text-[#94a3b8] font-bold uppercase tracking-tight">No voice users registered under this criteria</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((user, idx) => (
                  <tr key={user._id} className="hover:bg-[#f8fafc] transition-all duration-200 group">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-700 flex items-center justify-center text-white text-xl font-black shadow-lg shadow-indigo-500/20 group-hover:rotate-3 transition-transform`}>
                          {user.name ? user.name.charAt(0).toUpperCase() : <FaUser />}
                        </div>
                        <div>
                          <p className="text-base font-black text-[#1e293b] uppercase tracking-tight">{user.name || "Global Citizen"}</p>
                          <p className="text-[10px] text-[#94a3b8] font-extrabold uppercase mt-0.5 tracking-wider">Voice Registered</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap text-center">
                      <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-indigo-100 shadow-sm">
                        ACTIVE_VOICE
                      </span>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{user.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{user.phoneNo || 'N/A'}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col max-w-[250px]">
                        <span className="text-sm font-black text-[#1e293b] uppercase tracking-tighter truncate">{user.city || "Metropolis"}</span>
                        <span className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest italic">{user.residenceAddress || "Regional Office"}</span>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-xs text-indigo-400">
                          <FaCalendarAlt />
                        </div>
                        <span className="text-sm font-black text-[#1e293b]">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-8 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#64748b] font-bold">
              Entity Index {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-[#e2e8f0]">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronLeft size={12} />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TotalRaiseVoiceUser;